import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './TreeView.css';

// Datos del árbol de SIDUNEAWorld
export const siduneaTreeData = [
  {
    label: 'Manifiesto de Carga',
    children: [
      {
        label: 'Manejo de la Data',
        children: [
          {
            label: 'Captura Manual',
            children: [
              { label: 'Manifiesto' },
              { label: 'D/T', contextMenuItems: [
                { label: 'Nuevo' },
                { label: 'Buscar' },
                { label: 'Buscar D/Ts abandonado' },
                { label: 'Buscar todos los D/Ts pertenecientes a un manifiesto en particular' },
                { type: 'separator' },
                { label: 'Maximizar Todo' },
                { label: 'Minimizar Todo' },
                { type: 'separator' },
                { label: 'Cerrar' },
              ]},
              { label: 'Agrupar D/T' },
            ],
          },
          {
            label: 'Integración vía XML',
            children: [
              { label: 'Desagrupaje' },
            ],
          },
        ],
      },
      {
        label: 'Impresiones / Reportes',
        children: [
          {
            label: 'Impresiones',
            children: [
              { label: 'Manifiesto y D/T' },
              { label: 'D/T' },
            ],
          },
        ],
      },
      {
        label: 'Consultas Contenedores',
        children: [
          { label: 'Contenedores de un Manifiesto' },
          { label: 'Contenedores - D/T' },
        ],
      },
    ],
  },
  {
    label: 'Arancel Integrado',
    children: [
      { label: 'Clasificación y Regulación' },
      { label: 'Notas Documentales' },
      {
        label: 'Otros Componentes Imposición',
        children: [
          { label: 'Nombre Columnas Adicionales' },
          { label: 'Columnas Adicionales' },
          { label: 'Tarifas Estándar' },
          { label: 'Listas Imposición' },
          { label: 'Listas Imposición / Códigos A.' },
          { label: 'Listas Imposición / Empresas' },
        ],
      },
      {
        label: 'Reporte',
        children: [
          { label: 'Capítulos' },
          { label: 'Partidas' },
          { label: 'Código SA' },
          { label: 'Secciones' },
          { label: 'Sub Capítulos' },
          { label: 'Sub Partidas' },
        ],
      },
      {
        label: 'Componentes Arancel',
        children: [
          { label: 'Multas Globales' },
          { label: 'Calificadores Unidad de Medida' },
          { label: 'Preferencias' },
          { label: 'Preferencias / Países' },
          { label: 'Unidad de Medida' },
        ],
      },
    ],
  },
];

// Obtener todas las rutas de nodos con hijos (para expandir todo)
function getAllExpandableKeys(nodes, prefix = '') {
  let keys = [];
  nodes.forEach((node, i) => {
    const key = prefix ? `${prefix}/${node.label}` : node.label;
    if (node.children && node.children.length > 0) {
      keys.push(key);
      keys = keys.concat(getAllExpandableKeys(node.children, key));
    }
  });
  return keys;
}

function TreeNode({ node, path, expanded, toggleExpand, onItemClick, activeContextMenu, setActiveContextMenu, onContextAction }) {
  const key = path ? `${path}/${node.label}` : node.label;
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded.has(key);
  const hasContextMenu = node.contextMenuItems && node.contextMenuItems.length > 0;
  const isContextOpen = activeContextMenu === key;
  const rowRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const handleClick = () => {
    if (hasChildren) {
      toggleExpand(key);
    } else if (hasContextMenu) {
      if (!isContextOpen && rowRef.current) {
        const rect = rowRef.current.getBoundingClientRect();
        const menuWidth = 480;
        let left = rect.left;
        // Si el menú se sale por la derecha, ajustar hacia la izquierda
        if (left + menuWidth > window.innerWidth) {
          left = window.innerWidth - menuWidth - 10;
        }
        if (left < 0) left = 10;
        setMenuPos({ top: rect.bottom, left: left });
      }
      setActiveContextMenu(isContextOpen ? null : key);
    } else if (onItemClick) {
      onItemClick(node.label);
    }
  };

  return (
    <div className="tree-node">
      <div
        className="tree-node-row"
        ref={rowRef}
        onClick={handleClick}
      >
        {hasChildren ? (
          <span className="tree-node-toggle">{isExpanded ? '▼' : '►'}</span>
        ) : (
          <span className="tree-node-toggle tree-node-leaf" />
        )}
        <span className="tree-node-icon">{hasChildren ? '📁' : '📄'}</span>
        <span className="tree-node-label">{node.label}</span>
      </div>
      {isContextOpen && ReactDOM.createPortal(
        <div
          className="tree-context-menu"
          style={{ top: menuPos.top, left: menuPos.left }}
          onMouseDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
        >
          {node.contextMenuItems.map((opt, idx) =>
            opt.type === 'separator' ? (
              <div className="tree-context-separator" key={`sep-${idx}`} />
            ) : (
              <button
                className="tree-context-item"
                key={opt.label}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveContextMenu(null);
                  if (onContextAction) onContextAction(node.label, opt.label);
                }}
              >
                {opt.label}
              </button>
            )
          )}
        </div>,
        document.body
      )}
      {hasChildren && isExpanded && (
        <div className="tree-node-children">
          {node.children.map((child, i) => (
            <TreeNode
              key={child.label}
              node={child}
              path={key}
              expanded={expanded}
              toggleExpand={toggleExpand}
              onItemClick={onItemClick}
              activeContextMenu={activeContextMenu}
              setActiveContextMenu={setActiveContextMenu}
              onContextAction={onContextAction}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TreeView({ data, expandAll, onItemClick, onContextAction }) {
  const [expanded, setExpanded] = useState(new Set());
  const [activeContextMenu, setActiveContextMenu] = useState(null);

  useEffect(() => {
    if (expandAll) {
      setExpanded(new Set(getAllExpandableKeys(data)));
    }
  }, [expandAll, data]);

  useEffect(() => {
    function handleClick() {
      setActiveContextMenu(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="tree-view">
      {data.map((node, i) => (
        <TreeNode
          key={node.label}
          node={node}
          path=""
          expanded={expanded}
          toggleExpand={toggleExpand}
          onItemClick={onItemClick}
          activeContextMenu={activeContextMenu}
          setActiveContextMenu={setActiveContextMenu}
          onContextAction={onContextAction}
        />
      ))}
    </div>
  );
}

export default TreeView;
